<template>
  <div class="base-basic">
    <!-- 顶部导航栏 -->
    <div class="top-nav">
      <van-icon name="arrow-left" class="back-icon" @click="goBack" />
      <div class="nav-title">会议管理</div>
    </div>

    <!-- 主体内容 -->
    <div class="body-content">

      <!-- 会议导入卡片 -->
      <div class="meeting-card" v-if="isSuperAdmin">
        <div class="card-title">会议导入</div>
        <div class="card-content">
          <van-uploader :after-read="afterRead" accept=".xlsx,.xls" :max-count="1" class="custom-uploader">
            <van-button type="primary" block class="export-button">
              上传文件
            </van-button>
          </van-uploader>
        </div>
      </div>

      <div class="meeting-card" v-if="isSuperAdmin">
        <div class="card-title">会议更新</div>
        <div class="card-content">
          <van-uploader :after-read="afterReadUpdate" accept=".xlsx,.xls" :max-count="1" class="custom-uploader">
            <van-button type="primary" block class="export-button">
              上传文件
            </van-button>
          </van-uploader>
        </div>
      </div>

      <!-- 会议导出卡片 -->
      <div class="meeting-card">
        <div class="card-title">会议导出</div>
        <div class="card-content">
          <van-button type="primary" block class="export-button" @click="onClickExport">
            导出会议数据
          </van-button>
        </div>
      </div>

      <!-- 会议统计卡片（高亮特效） -->
      <div class="meeting-card card-highlight" v-if="isSuperAdmin">
        <div class="card-title">
          会议统计
          <span class="dot-new"></span>
        </div>
        <div class="card-content">
          <van-button type="primary" block class="export-button" @click="goToStatsReport">
            查看会议统计
          </van-button>
        </div>
      </div>

      <!-- 配额管理卡片 -->
      <div class="meeting-card" v-if="isSuperAdmin">
        <div class="card-title">配额管理</div>
        <div class="card-content">
          <van-button type="primary" block class="export-button" @click="goToQuotaManagement">
            查看配额管理
          </van-button>
        </div>
      </div>
    </div>

    <!-- 导入结果对话框 -->
    <van-dialog v-model:show="showResultDialog" title="导入结果" show-cancel-button cancel-button-text="取消"
      confirm-button-text="确认" @confirm="onConfirmResult" @cancel="onCancelResult" width="90%">
      <div class="result-content">
        <div class="result-item" v-if="importResult?.noDiffCount ">
          <span class="label">无需更新数:</span>
          <span class="value">{{ importResult?.noDiffCount || 0 }}</span>
        </div>
        <div class="result-item">
          <span class="label">导入成功数:</span>
          <span class="value">{{ importResult?.importedCount || 0 }}</span>
        </div>
        <div class="result-item">
          <span class="label">导入失败数:</span>
          <span class="value">{{ importResult?.failedCount || 0 }}</span>
        </div>
        <div v-if="importResult?.successes && importResult.successes.length > 0" class="errors-section">
          <div class="errors-title">成功详情:</div>
          <ul class="errors-list">
            <li v-for="(success, index) in importResult.successes" :key="index" class="error-item">
              {{ success }}
            </li>
          </ul>
        </div>
        <div v-if="importResult?.errors && importResult.errors.length > 0" class="errors-section">
          <div class="errors-title">失败详情:</div>
          <ul class="errors-list">
            <li v-for="(error, index) in importResult.errors" :key="index" class="error-item">
              {{ error }}
            </li>
          </ul>
        </div>
      </div>
    </van-dialog>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import axios from '@/api/axios';
import { Button, Dialog, showSuccessToast, showFailToast } from 'vant';

// 路由实例
const router = useRouter();

// 响应式数据
const isLoading = ref(false);
const showResultDialog = ref(false);
const importResult = ref(null);

// 回到上一页
const goBack = () => {
  router.back();
};

// 会议导入处理
const afterRead = async (file) => {
  isLoading.value = true;
  try {
    const formData = new FormData();
    formData.append('file', file.file);

    // 调用API上传文件
    const response = await axios.post('/api/meetings/batch-upload-meeting', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    if (response.status === 200) {
      // 设置导入结果并显示对话框
      importResult.value = response.data.data;
      showResultDialog.value = true;
    } else {
      showFailToast(`导入失败，状态码: ${response.status}`);
    }
  } catch (error) {
    if (error.response) {
      showFailToast(`导入失败: ${error.response.status} ${error.response.data?.message || ''}`);
    } else if (error.request) {
      showFailToast('网络异常，请检查网络连接');
    } else {
      showFailToast(`导入失败: ${error.message}`);
    }
  } finally {
    isLoading.value = false;
  }
};


const afterReadUpdate = async (file) => {
  isLoading.value = true;
  try {
    const formData = new FormData();
    formData.append('file', file.file);

    // 调用API上传文件
    const response = await axios.post('/api/meetings/batch-update-meeting', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    if (response.status === 200) {
      // 设置导入结果并显示对话框
      importResult.value = response.data.data;
      showResultDialog.value = true;
    } else {
      showFailToast(`导入失败，状态码: ${response.status}`);
    }
  } catch (error) {
    if (error.response) {
      showFailToast(`导入失败: ${error.response.status} ${error.response.data?.message || ''}`);
    } else if (error.request) {
      showFailToast('网络异常，请检查网络连接');
    } else {
      showFailToast(`导入失败: ${error.message}`);
    }
  } finally {
    isLoading.value = false;
  }
};


// 确认导入结果
const onConfirmResult = () => {
  showResultDialog.value = false;
  // 可以在这里添加确认后的逻辑
};

// 取消导入结果
const onCancelResult = () => {
  showResultDialog.value = false;
  // 可以在这里添加取消后的逻辑
};

// 会议导出处理
const onClickExport = async () => {
  // 直接调用接口导出全量会议数据
  try {
    const response = await axios.post('api/meetings/export', {}, { responseType: 'blob' });

    // 创建下载链接并触发下载
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `meetings-export-${new Date().getTime()}.xlsx`);
    document.body.appendChild(link);
    link.click();

    // 清理
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    showSuccessToast('导出成功');
  } catch (error) {
    console.error('导出会议数据失败:', error);
    showFailToast('导出会议数据失败');
  }
};

const isSuperAdmin = computed(() => {
  const userInfo = JSON.parse(localStorage.getItem('userInfo'));
  return userInfo.isSuperAdmin;
});

const goToQuotaManagement = () => {
  router.push('/quota-mgmt');
};

const goToStatsReport = () => {
  window.open('https://jinpaihsk.cn/stats-report/', '_blank');
};

</script>

<style scoped>
.base-basic {
  width: 100%;
  height: 100vh;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
}

.top-nav {
  display: flex;
  align-items: center;
  height: 48px;
  background-color: #fff;
  border-bottom: 1px solid #eee;
  padding: 0 15px;
  position: relative;
}

.back-icon {
  font-size: 20px;
  color: #333;
}

.nav-title {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.body-content {
  flex: 1;
  padding: 20px 15px;
  overflow-y: auto;
}

.meeting-card {
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.card-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin-bottom: 15px;
}

.card-content {
  display: flex;
  justify-content: center;
  align-items: center;
}

.export-button {
  background-color: #145294;
  border-radius: 4px;
  font-size: 16px;
  height: 44px;
  line-height: 44px;
  width: 250px;
}

.result-content {
  padding: 20px;
  text-align: left;
}

.result-item {
  margin-bottom: 10px;
  line-height: 1.5;
}

.label {
  color: #666;
}

.value {
  font-weight: 500;
  color: #333;
}

.errors-section {
  margin-top: 15px;
}

.errors-title {
  font-weight: 500;
  margin-bottom: 8px;
  color: #333;
}

.errors-list {
  background-color: #f5f5f5;
  padding: 10px;
  border-radius: 4px;
  font-size: 14px;
  max-height: 200px;
  overflow-y: auto;
}

.error-item {
  margin-bottom: 5px;
  color: #f5222d;
  line-height: 1.4;
}

/* ===== 会议统计卡片高亮特效 ===== */

@keyframes cardPulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(20, 82, 148, 0.08),
                0 2px 8px rgba(0,0,0,0.04);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(20, 82, 148, 0),
                0 0 16px 2px rgba(71, 128, 194, 0.12),
                0 2px 8px rgba(0,0,0,0.04);
  }
}

.card-highlight {
  position: relative;
  border: 1.5px solid #cce0f5;
  animation: cardPulse 2.5s ease-in-out infinite;
}

/* 绿色新增标识圆点 */
.dot-new {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #52c41a;
  margin-left: 6px;
  vertical-align: middle;
  animation: dotPulse 1.5s ease-in-out infinite;
}

@keyframes dotPulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.3); }
}
</style>